// 営業（design/README.md §7）。第1弾は手動リストのみ。AI提案・一括DM・営業資料AI生成は第3弾（未接続）。
// 営業リスト＝段階0（進行中エントリーが無い candidate）。「案件に追加」で打診中（段階1）のエントリーを作る（AddEntryModal）。
import { useState } from "react";
import type { GfTabProps } from "../../../types/gifting";
import { handleOf, listCandidates, mediaChip, primarySns } from "../../../lib/giftingRules";
import { GfButton, GfCard, GfCardHead, GfEmpty, GfNotConnected, GfTag } from "../ui/GfUi";
import { AddEntryModal } from "../modals/AddEntryModal";
import { CreatorFormModal } from "../modals/CreatorFormModal";

const TEMPLATES: Array<{ name: string; pages: string }> = [
  { name: "ブランド紹介", pages: "固定 6p" },
  { name: "案件提案書", pages: "固定4p＋AI 3p" },
  { name: "料率・条件シート", pages: "固定 2p" },
];

export function SalesTab(props: GfTabProps) {
  const { data, refresh } = props;
  const { board } = data;
  const list = listCandidates(board.creators, board.entries);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [addIds, setAddIds] = useState<string[] | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [tpl, setTpl] = useState(0);

  // 案件に追加済みで一覧から消えた人は選択から外す
  const pickedIds = list.filter((c) => picked.has(c.id)).map((c) => c.id);
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allPicked = list.length > 0 && pickedIds.length === list.length;
  const activeCampaigns = board.campaigns.filter((c) => c.status === "active");

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">営業</h1>
      </div>

      <div className="gf-grid-2 is-360 is-start">
        <GfCard className="gf-c18 gf-col-card">
          <GfCardHead
            title="営業リスト"
            right={
              <GfButton size="sm" onClick={() => setFormOpen(true)}>
                手動で追加
              </GfButton>
            }
          />
          <GfNotConnected title="AI提案・一括DM送信" phase="第3弾" note="類似アカウントの提案と、まとめての打診DM送信は未接続です。現在は手動登録したクリエイターを案件に追加します。" />
          <div className="gf-bulkbar">
            <span className="gf-ink2">{pickedIds.length > 0 ? `${pickedIds.length}件を選択中` : "チェックした相手をまとめて案件に追加できます"}</span>
            <GfButton variant="primary" size="sm" disabled={pickedIds.length === 0} onClick={() => setAddIds(pickedIds)}>
              選択した相手を案件に追加（打診中）
            </GfButton>
          </div>

          {list.length === 0 ? (
            <GfEmpty>営業リストは空です。「手動で追加」からクリエイターを登録できます。</GfEmpty>
          ) : (
            <>
              <div className="gf-sales-selectall">
                <button type="button" className="gf-link-btn gf-small" onClick={() => setPicked(allPicked ? new Set() : new Set(list.map((c) => c.id)))}>
                  {allPicked ? "選択を解除" : "すべて選択"}
                </button>
              </div>
              {list.map((c) => {
                const main = primarySns(c.sns);
                return (
                  <div className="gf-sales-row" key={c.id}>
                    <input
                      type="checkbox"
                      className="gf-sales-check"
                      aria-label={`${c.name} を選択`}
                      checked={picked.has(c.id)}
                      onChange={() => toggle(c.id)}
                    />
                    <span className="gf-sales-main">
                      <span className="gf-sales-name">
                        {c.name}
                        {main && <span className="gf-media-chip">{mediaChip(main.platform, main.follower_count)}</span>}
                      </span>
                      <span className="gf-note">
                        {[c.genre, handleOf(main), c.source === "inbound" ? "先方から連絡" : "こちらから打診"].filter(Boolean).join("・")}
                      </span>
                    </span>
                    <GfButton size="sm" onClick={() => setAddIds([c.id])}>
                      案件に追加
                    </GfButton>
                  </div>
                );
              })}
            </>
          )}
          <div className="gf-list-foot">リスト合計 {list.length}件</div>
        </GfCard>

        <GfCard className="gf-c18 gf-col-card gf-col-card-14">
          <GfCardHead title="営業資料" right={<GfTag tone="muted">未接続・第3弾で接続予定</GfTag>} />
          <div className="gf-tpl-grid">
            {TEMPLATES.map((t, i) => (
              <button type="button" key={t.name} className={`gf-tpl${tpl === i ? " is-active" : ""}`} aria-pressed={tpl === i} onClick={() => setTpl(i)}>
                <span className="gf-tpl-thumb" />
                <span className="gf-tpl-name">{t.name}</span>
                <span className="gf-note">{t.pages}</span>
              </button>
            ))}
          </div>
          <div className="gf-form-grid gf-form-grid-2">
            <label className="gf-field">
              <span className="gf-field-label">送付先</span>
              <select className="gf-select" disabled>
                <option>{list[0] ? `${list[0].name}${handleOf(primarySns(list[0].sns)) ? `（${handleOf(primarySns(list[0].sns))}）` : ""}` : "—"}</option>
              </select>
            </label>
            <label className="gf-field">
              <span className="gf-field-label">案件</span>
              <select className="gf-select" disabled>
                <option>{activeCampaigns[0]?.name ?? "—"}</option>
              </select>
            </label>
          </div>
          <GfButton variant="primary" block disabled>
            AIで資料を生成
          </GfButton>
          <GfNotConnected title="営業資料のAI生成" phase="第3弾" note="テンプレートの選択だけ試せます。固定ページ＋AI可変ページの自動生成は未接続です。" />
        </GfCard>
      </div>

      <CreatorFormModal
        {...props}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => setFormOpen(false)}
      />
      <AddEntryModal
        {...props}
        open={addIds !== null}
        onClose={() => setAddIds(null)}
        influencerIds={addIds ?? []}
        onSaved={() => {
          setAddIds(null);
          setPicked(new Set());
          void refresh();
        }}
      />
    </>
  );
}
